/** Pure rules for GET /api/me/access. Does not treat assigned_profile.permissions as effective. */

export function normalizeAccess(payload, { failed = false } = {}) {
  if (failed || !payload || typeof payload !== 'object') {
    return {
      ready: true,
      failed: true,
      role: null,
      profileEnforced: false,
      authorizationMode: null,
      accessStatus: null,
      permissions: null,
      warehouses: [],
      allWarehouses: false,
      defaultWarehouseId: null,
      legacyWarehouseId: null,
      profilesEnabled: false,
    }
  }

  const role = payload.role || null
  const profileEnforced = payload.profile_enforced === true
  const permissions = Array.isArray(payload.effective_permissions) ? payload.effective_permissions : null

  return {
    ready: true,
    failed: false,
    role,
    profileEnforced,
    authorizationMode: payload.authorization_mode || null,
    accessStatus: payload.access_status || null,
    permissions,
    warehouses: Array.isArray(payload.warehouses) ? payload.warehouses : [],
    allWarehouses: payload.all_warehouses === true,
    defaultWarehouseId: payload.default_warehouse_id || null,
    legacyWarehouseId: payload.legacy_warehouse_id || null,
    profilesEnabled: payload.profiles_enabled === true,
    isAdmin: role === 'admin',
  }
}

export function isProfileRequired(access) {
  return Boolean(access && access.profileEnforced && access.accessStatus === 'profile_required')
}

export function canUseOperation(access) {
  if (!access || !access.ready || access.failed) return false
  if (isProfileRequired(access)) return false
  return true
}

/**
 * Admin and legacy employees are not filtered by effective_permissions.
 * A failed load never falls back to legacy.
 */
export function hasPermission(access, permission) {
  if (!access || access.failed || !access.ready) return false
  if (isProfileRequired(access)) return false
  if (access.isAdmin || access.role === 'admin') return true
  if (!access.profileEnforced) return true
  if (!Array.isArray(access.permissions)) return false
  return access.permissions.includes(permission)
}

export function hasAnyPermission(access, permissions) {
  if (!permissions || !permissions.length) return hasPermission(access, '')
  if (!access || access.failed || !access.ready) return false
  if (isProfileRequired(access)) return false
  if (access.isAdmin || access.role === 'admin' || !access.profileEnforced) return true
  return permissions.some((permission) => hasPermission(access, permission))
}

/**
 * Company copies plus global templates the company has not copied yet.
 * A template and its copy share template_key; only the copy stays.
 */
export function profileChoices(profileRows, templateRows = []) {
  const rows = Array.isArray(profileRows) ? profileRows : []
  const extra = Array.isArray(templateRows) ? templateRows : []
  const companyProfiles = rows.filter((row) => row && row.id != null && row.company_id != null && !row.is_template)
  const copied = new Set(companyProfiles.map((row) => row.template_key).filter(Boolean))
  const seen = new Set()
  const templates = []
  ;[...rows, ...extra].forEach((row) => {
    if (!row || row.id == null || row.company_id != null || !row.template_key) return
    if (copied.has(row.template_key) || seen.has(row.template_key)) return
    seen.add(row.template_key)
    templates.push(row)
  })

  return [
    ...companyProfiles.map((row) => ({
      value: String(row.id),
      name: row.name,
      templateKey: row.template_key || null,
    })),
    ...templates.map((row) => ({
      value: String(row.id),
      name: row.name,
      templateKey: row.template_key,
    })),
  ]
}

export function pickWarehouseId(access, currentId) {
  if (!access || access.failed || !access.profileEnforced || access.isAdmin || access.role === 'admin') {
    return currentId || null
  }
  if (isProfileRequired(access)) return null
  if (access.allWarehouses) return currentId || access.defaultWarehouseId || null
  const ids = (access.warehouses || []).map((row) => Number(row.id))
  if (!ids.length) return null
  if (ids.length === 1) return ids[0]
  if (currentId && ids.includes(Number(currentId))) return Number(currentId)
  if (access.defaultWarehouseId && ids.includes(Number(access.defaultWarehouseId))) {
    return Number(access.defaultWarehouseId)
  }
  return ids[0]
}

/**
 * Warehouse options for a form. An existing document keeps its stored warehouse
 * even when that id is no longer authorized, so the form does not swap it.
 */
export function scopeWarehouseRows(access, rows, documentWarehouseId = null) {
  const list = Array.isArray(rows) ? rows.filter(Boolean) : []
  if (!access || access.failed || !access.profileEnforced || access.isAdmin || access.role === 'admin' || access.allWarehouses) {
    return list
  }
  const ids = new Set((access.warehouses || []).map((row) => Number(row.id)))
  const scoped = list.filter((row) => ids.has(Number(row.id)))
  if (documentWarehouseId == null || documentWarehouseId === '') return scoped
  const current = Number(documentWarehouseId)
  if (!current || ids.has(current)) return scoped
  const existing = list.find((row) => Number(row.id) === current)
  return existing ? [...scoped, existing] : scoped
}

const LIST_WRITE = [
  ['clients', 'customer.update'],
  ['vendors', 'vendor.update'],
  ['items', 'catalog.update'],
  ['brands', 'catalog.update'],
  ['product-tags', 'catalog.update'],
  ['badges', 'catalog.update'],
  ['attribute-definitions', 'catalog.update'],
  ['categories', 'catalog.update'],
  ['uom', 'catalog.update'],
  ['locations', 'catalog.update'],
  ['expense-category', 'expense.draft'],
]

export function listWritePermission(apiUrl) {
  const url = String(apiUrl || '').split('?')[0]
  const match = LIST_WRITE.find(([prefix]) => url === prefix || url.startsWith(`${prefix}/`))
  return match ? match[1] : null
}

export function formatAmountOrHide(value) {
  if (value === null || value === undefined || value === '') return null
  return value
}

const ROUTE_ANY = [
  ['/products/new', ['catalog.create']],
  ['/labors/new', ['catalog.create']],
  ['/expenses/new', ['expense.draft']],
  ['/online-sales', ['invoice.view']],
  ['/invoices', ['invoice.view']],
  ['/recurring-invoices', ['invoice.view']],
  ['/storefront-inquiries', ['invoice.view']],
  ['/brands', ['catalog.view']],
  ['/product-tags', ['catalog.view']],
  ['/product-badges', ['catalog.view']],
  ['/attribute-definitions', ['catalog.view']],
  ['/categories', ['catalog.view']],
  ['/uoms', ['catalog.view']],
  ['/labors', ['catalog.view']],
  ['/devices', ['work_order.view']],
  ['/device-types', ['work_order.view']],
  ['/expense-categories', ['expense.view']],
  ['/payment-accounts', ['payment.view']],
  ['/payment-types', ['payment.view']],
  ['/estimates', ['estimate.view']],
  ['/payments', ['payment.view']],
  ['/receive-payments', ['payment.view']],
  ['/receipt-payments', ['payment.view']],
  ['/work-orders', ['work_order.view']],
  ['/expenses', ['expense.view']],
  ['/customers', ['customer.view']],
  ['/vendors', ['vendor.view']],
  ['/products', ['catalog.view']],
  ['/marketing/social-content', ['catalog.view', 'catalog.update']],
  ['/marketing/creative-studio', ['catalog.view', 'catalog.update']],
  ['/settings/social-content', []],
  ['/stock-receipts', ['stock_receipt.view']],
  ['/stock-transfers', ['transfer.view']],
  ['/adjustments', ['adjustment.view']],
  ['/purchase-orders', ['purchase_order.view']],
  ['/vendor-bills', ['vendor_bill.view']],
  ['/inventory-report', ['report.view']],
  ['/pos', ['pos.view']],
  ['/accounting', ['report.view']],
  ['/settings/chart-of-accounts', ['report.view']],
  ['/settings/subscription', []],
  ['/warehouses', ['stock_receipt.view', 'transfer.view', 'adjustment.view']],
  ['/settings/account', []],
  ['/taxes', []],
  ['/settings/payment', []],
  ['/settings/sms', []],
]

function isProductEditPath(pathname) {
  return /^\/products\/[^/]+\/edit\/?$/.test(pathname)
}

function isWarehouseManagePath(pathname) {
  return pathname === '/warehouses/new'
    || pathname.startsWith('/warehouses/new/')
    || /^\/warehouses\/[^/]+\/edit\/?$/.test(pathname)
}

export function routeAllowed(pathname, access) {
  if (!access || !access.ready) return false
  if (access.failed || isProfileRequired(access)) return false
  if (access.isAdmin || access.role === 'admin' || !access.profileEnforced) return true
  if (isProductEditPath(pathname) && !hasPermission(access, 'catalog.update')) return false
  if (isWarehouseManagePath(pathname)) return false
  const match = ROUTE_ANY.find(([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`))
  if (!match) return true
  if (!match[1].length) return false
  return hasAnyPermission(access, match[1])
}

export const SIDEBAR_ANY = {
  Products: ['catalog.view'],
  Sales: ['invoice.view', 'estimate.view', 'payment.view', 'work_order.view'],
  Costumers: ['customer.view'],
  Vendor: ['vendor.view'],
  'Stock Receipt': ['stock_receipt.view'],
  'Stock Transfer': ['transfer.view'],
  Adjustment: ['adjustment.view'],
  'Purchase Order': ['purchase_order.view'],
  Expensive: ['expense.view', 'payment.view'],
  Reports: ['report.view'],
  pos: ['pos.view'],
  WareHouse: ['stock_receipt.view', 'transfer.view', 'adjustment.view'],
}

export function sidebarTokenAllowed(token, access) {
  if (!access || !access.ready || access.failed) return false
  if (isProfileRequired(access)) return false
  if (access.isAdmin || access.role === 'admin' || !access.profileEnforced) return true
  const keys = SIDEBAR_ANY[token]
  if (!keys) return true
  if (!keys.length) return false
  return hasAnyPermission(access, keys)
}
